import { RACK_SIZE, RANKS, SUITS } from "./constants.js";
import type { Card, MatchPlayer, MatchState, Rank, Seat } from "./types.js";

export class GameRuleError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "GameRuleError";
  }
}

export type RandomSource = () => number;

export interface PlayerSeed {
  id: string;
  name: string;
  seat: Seat;
}

function now(): number {
  return Date.now();
}

export function createDeck(): Card[] {
  return SUITS.flatMap((suit) =>
    RANKS.map((rank) => ({
      id: `${suit}-${rank}`,
      rank,
      suit,
    })),
  );
}

export function shuffle<T>(items: readonly T[], random: RandomSource = Math.random): T[] {
  const result = [...items];

  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }

  return result;
}

export function areAdjacent(first: Rank, second: Rank): boolean {
  const distance = Math.abs(first - second);
  return distance === 1 || distance === 12;
}

export function canPlay(card: Card, pileTop: Card | null): boolean {
  return pileTop !== null && areAdjacent(card.rank, pileTop.rank);
}

export function legalPiles(card: Card, match: MatchState): Array<0 | 1> {
  const legal: Array<0 | 1> = [];
  const firstTop = match.piles[0].at(-1) ?? null;
  const secondTop = match.piles[1].at(-1) ?? null;

  if (canPlay(card, firstTop)) legal.push(0);
  if (canPlay(card, secondTop)) legal.push(1);

  return legal;
}

function draw(stock: Card[]): Card | null {
  return stock.pop() ?? null;
}

function createPlayer(seed: PlayerSeed, cards: Card[]): MatchPlayer {
  const rack = Array.from({ length: RACK_SIZE }, () => draw(cards));

  return {
    ...seed,
    rack,
    stock: cards,
    capturedCount: 0,
    chinCount: 0,
    falseChinCount: 0,
    aidReceived: false,
  };
}

function topRanksMatch(piles: MatchState["piles"]): boolean {
  const first = piles[0].at(-1);
  const second = piles[1].at(-1);
  return first !== undefined && second !== undefined && first.rank === second.rank;
}

function hasLegalMove(player: MatchPlayer, match: MatchState): boolean {
  return player.rack.some((card) => card !== null && legalPiles(card, match).length > 0);
}

export function isStalled(match: MatchState): boolean {
  if (match.phase !== "playing" || match.chinAvailable) return false;
  return !match.players.some((player) => hasLegalMove(player, match));
}

function clonePlayer(player: MatchPlayer): MatchPlayer {
  return {
    ...player,
    rack: [...player.rack],
    stock: [...player.stock],
  };
}

export function cloneMatch(match: MatchState): MatchState {
  return {
    ...match,
    players: [clonePlayer(match.players[0]), clonePlayer(match.players[1])],
    piles: [[...match.piles[0]], [...match.piles[1]]],
    lastEvent: { ...match.lastEvent },
  };
}

function activeCardCount(player: MatchPlayer): number {
  return player.stock.length + player.rack.filter(Boolean).length;
}

function updateDerived(match: MatchState): void {
  match.chinAvailable = topRanksMatch(match.piles);
  match.stalled = isStalled(match);
}

function finishIfNeeded(match: MatchState): void {
  const emptyPlayers = match.players.filter((player) => activeCardCount(player) === 0);
  if (emptyPlayers.length === 0) return;

  match.phase = "finished";
  match.winnerId = emptyPlayers.length === 1 ? emptyPlayers[0].id : null;
  match.finishedAt = now();
  match.lastEvent = {
    type: "match-finished",
    at: match.finishedAt,
    winnerId: match.winnerId,
  };
}

/**
 * La ayuda se aplica una sola vez: si un jugador agotó el mazo pero todavía
 * conserva cartas boca arriba, el rival le cede una carta. Evita un bucle
 * infinito y conserva la regla social de la variante descrita por el usuario.
 */
function grantMandatoryAid(match: MatchState, recipient: MatchPlayer): void {
  if (recipient.stock.length > 0 || recipient.rack.every((card) => card === null) || recipient.aidReceived) {
    return;
  }

  const donor = match.players.find((player) => player.id !== recipient.id);
  if (!donor || donor.stock.length === 0) return;

  const donatedCard = donor.stock.pop();
  if (!donatedCard) return;

  recipient.stock.push(donatedCard);
  recipient.aidReceived = true;
  match.lastEvent = {
    type: "aid-given",
    at: now(),
    actorId: donor.id,
    recipientId: recipient.id,
  };
}

function takeStarter(player: MatchPlayer): Card | null {
  const stockCard = draw(player.stock);
  if (stockCard) return stockCard;

  const rackIndex = player.rack.findIndex((card) => card !== null);
  if (rackIndex === -1) return null;

  const rackCard = player.rack[rackIndex];
  player.rack[rackIndex] = null;
  return rackCard;
}

function seedCenter(match: MatchState): void {
  for (const player of match.players) grantMandatoryAid(match, player);

  const first = takeStarter(match.players[0]);
  const second = takeStarter(match.players[1]);

  if (first) match.piles[0].push(first);
  if (second) match.piles[1].push(second);
}

export function startMatch(
  playerSeeds: [PlayerSeed, PlayerSeed],
  random: RandomSource = Math.random,
): MatchState {
  const deck = shuffle(createDeck(), random);
  const half = deck.length / 2;
  const firstCards = deck.slice(0, half);
  const secondCards = deck.slice(half);
  const startedAt = now();

  const match: MatchState = {
    phase: "playing",
    revision: 1,
    players: [createPlayer(playerSeeds[0], firstCards), createPlayer(playerSeeds[1], secondCards)],
    piles: [[], []],
    chinAvailable: false,
    stalled: false,
    winnerId: null,
    lastEvent: { type: "match-started", at: startedAt },
    startedAt,
  };

  seedCenter(match);
  updateDerived(match);
  return match;
}

function getPlayer(match: MatchState, playerId: string): MatchPlayer {
  const player = match.players.find((candidate) => candidate.id === playerId);
  if (!player) throw new GameRuleError("PLAYER_NOT_FOUND", "Ese jugador no pertenece a la partida.");
  return player;
}

function assertPlaying(match: MatchState): void {
  if (match.phase !== "playing") {
    throw new GameRuleError("MATCH_NOT_PLAYING", "La partida no está en juego.");
  }
}

export function playCard(
  current: MatchState,
  playerId: string,
  cardId: string,
  pileIndex: 0 | 1,
  expectedTopCardId?: string,
): MatchState {
  assertPlaying(current);
  const match = cloneMatch(current);
  const player = getPlayer(match, playerId);
  const rackIndex = player.rack.findIndex((card) => card?.id === cardId);

  if (rackIndex === -1) {
    throw new GameRuleError("CARD_NOT_IN_RACK", "Esa carta ya no está disponible.");
  }

  const card = player.rack[rackIndex];
  const pileTop = match.piles[pileIndex].at(-1) ?? null;

  if (expectedTopCardId !== undefined && pileTop?.id !== expectedTopCardId) {
    throw new GameRuleError("STALE_TOP", "La carta del centro cambió antes de tu jugada.");
  }

  if (!card || !canPlay(card, pileTop)) {
    throw new GameRuleError("INVALID_SEQUENCE", "La carta debe ser inmediatamente mayor o menor.");
  }

  match.piles[pileIndex].push(card);
  player.rack[rackIndex] = draw(player.stock);
  match.lastEvent = {
    type: "card-played",
    at: now(),
    actorId: playerId,
    card,
    pileIndex,
  };

  finishIfNeeded(match);
  if (match.phase === "playing") {
    grantMandatoryAid(match, player);
    updateDerived(match);
  }
  match.revision += 1;
  return match;
}

export function claimChin(current: MatchState, playerId: string): MatchState {
  assertPlaying(current);
  const match = cloneMatch(current);
  const player = getPlayer(match, playerId);
  const correct = topRanksMatch(match.piles);
  const opponent = match.players.find((candidate) => candidate.id !== playerId);
  if (!opponent) throw new GameRuleError("PLAYER_NOT_FOUND", "Falta el rival de esta partida.");

  const penalizedPlayer = correct ? opponent : player;
  const collectedCards = [...match.piles[0], ...match.piles[1]];
  const capturedCount = match.piles[0].length + match.piles[1].length;
  penalizedPlayer.stock.push(...shuffle(collectedCards));
  if (correct) {
    player.capturedCount += capturedCount;
    player.chinCount += 1;
  } else {
    player.falseChinCount += 1;
  }
  match.piles = [[], []];
  match.lastEvent = {
    type: "chin-claimed",
    at: now(),
    actorId: playerId,
    capturedCount,
    penalizedPlayerId: penalizedPlayer.id,
    correct,
  };

  seedCenter(match);
  finishIfNeeded(match);
  if (match.phase === "playing") updateDerived(match);
  match.revision += 1;
  return match;
}

export function refreshCenter(current: MatchState): MatchState {
  assertPlaying(current);
  if (!isStalled(current)) {
    throw new GameRuleError("MOVES_AVAILABLE", "Todavía queda al menos una jugada posible.");
  }

  const match = cloneMatch(current);
  seedCenter(match);
  match.lastEvent = { type: "center-refreshed", at: now() };
  finishIfNeeded(match);
  if (match.phase === "playing") updateDerived(match);
  match.revision += 1;
  return match;
}
